import React, { useState, useEffect } from "react";
import {
  Key,
  Search,
  RefreshCw,
  Monitor,
  User,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
  X,
  Check,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import { toast } from "sonner";

export default function MasterAdminLicenses() {
  const { masterApi } = useMasterAdmin();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [reassignLic, setReassignLic] = useState(null);
  const [targetUsername, setTargetUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const res = await masterApi.get("/master-admin/licenses", {
        params: { query: searchQuery },
      });
      setLicenses(res.data.licenses || []);
    } catch (err) {
      toast.error("Failed to load licenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, []);

  const handleUnbind = async (lic) => {
    if (!window.confirm(`Unbind PC (${lic.machine_name || "Device"}) from license ${lic.license_id}?`)) {
      return;
    }
    try {
      await masterApi.post(`/master-admin/licenses/${lic.license_id}/unbind-device`);
      toast.success(`Device unbound from license ${lic.license_id}`);
      fetchLicenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unbind device");
    }
  };

  const handleToggleStatus = async (lic) => {
    const nextStatus = lic.status === "active" ? "blocked" : "active";
    try {
      const formData = new FormData();
      formData.append("status", nextStatus);
      await masterApi.put(`/master-admin/licenses/${lic.license_id}/status`, formData);
      toast.success(`License status set to ${nextStatus}`);
      fetchLicenses();
    } catch (err) {
      toast.error("Failed to update license status");
    }
  };

  const handleReassign = async (e) => {
    e.preventDefault();
    if (!reassignLic || !targetUsername.trim()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("target_username", targetUsername.trim());
      const res = await masterApi.post(`/master-admin/licenses/${reassignLic.license_id}/reassign`, formData);
      toast.success(res.data.message || "License reassigned successfully");
      setReassignLic(null);
      setTargetUsername("");
      fetchLicenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign license");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <span>Hardware Licenses</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 font-bold">
              1 PC = 1 License
            </span>
          </h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Physical device hardware binding and activation management.
          </p>
        </div>

        <button
          onClick={fetchLicenses}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-semibold text-zinc-300 hover:text-white transition-all self-start sm:self-auto"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Licenses Table */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-zinc-400">
            <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : licenses.length === 0 ? (
          <div className="p-10 text-center text-xs text-zinc-500">
            No license records found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-zinc-950/60 text-zinc-400 border-b border-zinc-800 font-medium">
                  <th className="py-3 px-4">License ID</th>
                  <th className="py-3 px-4">Assigned User</th>
                  <th className="py-3 px-4">Bound PC Hardware</th>
                  <th className="py-3 px-4">Expiry Date</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {licenses.map((lic) => {
                  const isBound = Boolean(lic.machine_name);
                  const isBlocked = lic.status === "blocked";
                  return (
                    <tr key={lic.license_id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-white">{lic.license_id}</td>
                      <td className="py-3.5 px-4 font-mono text-zinc-300">@{lic.username}</td>
                      <td className="py-3.5 px-4">
                        {isBound ? (
                          <div className="flex items-center gap-2">
                            <Monitor className="w-4 h-4 text-purple-400 shrink-0" />
                            <div>
                              <div className="font-bold text-white">{lic.machine_name}</div>
                              <div className="text-[10px] text-zinc-500 font-mono truncate max-w-xs">
                                {lic.device_fingerprint ? `${lic.device_fingerprint.slice(0, 16)}...` : ""}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-zinc-500 italic">Unbound (Ready for first PC login)</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 font-mono text-zinc-400">
                        {lic.expires_at ? new Date(lic.expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                            isBlocked
                              ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                              : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isBlocked ? "bg-rose-400" : "bg-emerald-400"}`} />
                          <span className="capitalize">{lic.status}</span>
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {isBound && (
                            <button
                              onClick={() => handleUnbind(lic)}
                              className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-medium transition-colors"
                              title="Unbind hardware PC"
                            >
                              Unbind PC
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setReassignLic(lic);
                              setTargetUsername("");
                            }}
                            className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] font-medium transition-colors"
                            title="Reassign to another user"
                          >
                            Reassign
                          </button>

                          <button
                            onClick={() => handleToggleStatus(lic)}
                            className={`p-1.5 rounded-lg border transition-colors ${
                              isBlocked
                                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                                : "bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
                            }`}
                            title={isBlocked ? "Unblock License" : "Block License"}
                          >
                            {isBlocked ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Reassign License */}
      {reassignLic && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-bold text-white">Reassign License</h3>
              </div>
              <button
                onClick={() => setReassignLic(null)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleReassign} className="flex flex-col gap-3 text-xs">
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 flex flex-col gap-1">
                <span className="text-zinc-400">License: <strong className="text-white font-mono">{reassignLic.license_id}</strong></span>
                <span className="text-zinc-400">Current Owner: <strong className="text-zinc-300 font-mono">@{reassignLic.username}</strong></span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Target Username *</label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={targetUsername}
                  onChange={(e) => setTargetUsername(e.target.value)}
                  placeholder="e.g. JSM002"
                  className="px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono text-sm"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setReassignLic(null)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 font-semibold hover:bg-zinc-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !targetUsername.trim()}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 font-bold hover:brightness-110 shadow-md shadow-amber-500/20"
                >
                  {submitting ? "Reassigning..." : "Confirm Reassign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
